import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow, format } from "date-fns";
import { FeedbackStats, Feedback, ReceiptRequest, ParkingLotMapItem, Geometry, ParkingLot } from "./types";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatRelativeTime(dateString: string): string {
    try {
        const date = new Date(dateString);
        return formatDistanceToNow(date, { addSuffix: true });
    } catch {
        return 'Unknown time';
    }
}

export function calculateFeedbackSummary(feedback: Feedback[] | undefined | null): FeedbackStats {
    const list = feedback ?? [];
    const thumbsUp = list.filter(f => f.feedbackType === 'up').length;
    const thumbsDown = list.filter(f => f.feedbackType === 'down').length;
    const total = thumbsUp + thumbsDown;
    const positivePercentage = total > 0 ? Math.round((thumbsUp / total) * 100) : 0;

    return {
        thumbsUp,
        thumbsDown,
        total,
        positivePercentage,
    };
}

export function formatRating(rating: number): string {
    return `${(rating * 100).toFixed(0)}%`;
}

export function getRatingColor(rating: number): string {
    if (rating >= 0.8) return 'text-green-600';
    if (rating >= 0.6) return 'text-yellow-600';
    if (rating >= 0.4) return 'text-orange-600';
    return 'text-red-600';
}

export function formatRelativeDate(dateString: string): string {
    try {
        const date = new Date(dateString);
        return formatDistanceToNow(date, { addSuffix: true });
    } catch {
        return 'Unknown date';
    }
}

export function formatDate(dateString: string): string {
    try {
        const date = new Date(dateString);
        return format(date, 'MMM dd, yyyy HH:mm');
    } catch {
        return 'Invalid date';
    }
}

export const formatTimeAgo = (timestamp: string): string => {
    try {
        return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
        return 'Unknown time';
    }
};

export const formatISOTime = (timestamp: string): string => {
    try {
        return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
        return 'Invalid date';
    }
};

export const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
};

export const formatNumber = (value: number): string => {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
};

export const getStatusColor = (status: string): string => {
    switch (status) {
        case 'new':
            return 'text-blue-600 bg-blue-100';
        case 'growing':
            return 'text-green-600 bg-green-100';
        case 'stable':
            return 'text-yellow-600 bg-yellow-100';
        case 'declining':
            return 'text-red-600 bg-red-100';
        default:
            return 'text-gray-600 bg-gray-100';
    }
};

export const getTrendColor = (trend: string): string => {
    switch (trend) {
        case 'improving':
            return 'text-green-600 bg-green-100';
        case 'stable':
            return 'text-yellow-600 bg-yellow-100';
        case 'declining':
            return 'text-red-600 bg-red-100';
        default:
            return 'text-gray-600 bg-gray-100';
    }
};

// Danish number formatting (comma as decimal separator)
export const formatDanishNumber = (value: number, decimals: number = 2): string => {
    return new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
};

// Session status badge color mapping
export const getSessionStatusColor = (status: string): string => {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-800';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'completed':
            return 'bg-gray-100 text-gray-800';
        case 'completed_with_refund':
            return 'bg-blue-100 text-blue-800';
        case 'cancelled':
        case 'cancelled_before_start':
            return 'bg-red-100 text-red-800';
        case 'failed':
        case 'completed_charge_failed':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Payment status badge color mapping
export const getPaymentStatusColor = (status: string): string => {
    switch (status) {
        case 'charged':
            return 'bg-green-100 text-green-800';
        case 'authorized':
            return 'bg-blue-100 text-blue-800';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'failed':
            return 'bg-red-100 text-red-800';
        case 'refunded':
        case 'partial_refund':
            return 'bg-purple-100 text-purple-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Receipt request status badge color mapping
export const getReceiptStatusColor = (status: string): string => {
    switch (status) {
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'sent':
            return 'bg-green-100 text-green-800';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Format license plate with spaces (e.g., "AB12345" -> "AB 12 345")
export const formatLicensePlate = (plate: string): string => {
    if (!plate) return '';
    // Remove existing spaces and format as Danish license plate
    const cleaned = plate.replace(/\s/g, '').toUpperCase();
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`;
};

// Format Danish date (e.g., "26 Nov 2025, 14:00")
export const formatDanishDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return format(date, 'd MMM yyyy, HH:mm');
    } catch {
        return 'Invalid date';
    }
};

// Refund status badge color mapping
export const getRefundStatusColor = (status?: string): string => {
    if (!status) return '';
    switch (status) {
        case 'refund_initiated':
            return 'bg-yellow-100 text-yellow-800';
        case 'refund_completed':
            return 'bg-green-100 text-green-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Format refund status for display
export const formatRefundStatus = (status?: string): string => {
    if (!status) return '';
    return status.replace(/_/g, ' ');
};

// Generate email template for receipt
export const generateReceiptEmailTemplate = (request: ReceiptRequest): string => {
    const { sessionSnapshot, email, createdAt } = request;
    const { 
        receiptNumber, 
        lotName, 
        lotId,
        areaNo,
        licensePlate, 
        startTime, 
        endTime, 
        durationMinutes, 
        pricing, 
        maskedPan, 
        cardType 
    } = sessionSnapshot;

    const location = lotName || lotId || 'N/A';
    const formattedStartTime = formatDanishDate(startTime);
    const formattedEndTime = formatDanishDate(endTime);
    const formattedRequestTime = formatDanishDate(createdAt);

    return `Kære kunde,

Tak for at du brugte FreeSpot til din parkering. Her er din kvittering:

KVITTERING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Kvitteringsnummer: ${receiptNumber}

PARKERINGSOPLYSNINGER
Lokation: ${location}${areaNo ? ` (Zone ${areaNo})` : ''}
Nummerplade: ${formatLicensePlate(licensePlate)}
Start: ${formattedStartTime}
Slut: ${formattedEndTime}
Varighed: ${durationMinutes} minutter

BETALING
Parkeringsafgift: ${formatDanishNumber(pricing.parkingCost)} ${pricing.currency}
Servicegebyr: ${formatDanishNumber(pricing.serviceFee)} ${pricing.currency}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total: ${formatDanishNumber(pricing.total)} ${pricing.currency}

Betalingsmetode: ${cardType || 'Kort'} ${maskedPan ? `(${maskedPan.slice(-4)})` : ''}

Kvittering anmodet: ${formattedRequestTime}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Med venlig hilsen,
FreeSpot

---
Denne kvittering er sendt til: ${email}
`;
};

// Google Maps and GeoJSON conversion utilities
export interface GoogleMapsLatLng {
    lat: number;
    lng: number;
}

const coordinatePathToGoogleMaps = (coordinates: unknown): GoogleMapsLatLng[] => {
    if (!Array.isArray(coordinates)) return [];
    return coordinates.flatMap((coordinate): GoogleMapsLatLng[] => {
        if (
            !Array.isArray(coordinate) ||
            typeof coordinate[0] !== 'number' ||
            typeof coordinate[1] !== 'number'
        ) {
            return [];
        }
        return [{ lat: coordinate[1], lng: coordinate[0] }];
    });
};

/** Returns each path in a GeoJSON LineString/MultiLineString without closing it. */
export const extractLineGeometryPaths = (
    geometry?: Geometry | null
): GoogleMapsLatLng[][] => {
    if (!geometry?.coordinates) return [];
    if (geometry.type === 'LineString') {
        const path = coordinatePathToGoogleMaps(geometry.coordinates);
        return path.length >= 2 ? [path] : [];
    }
    if (geometry.type === 'MultiLineString') {
        return (geometry.coordinates as number[][][])
            .map(coordinatePathToGoogleMaps)
            .filter((path) => path.length >= 2);
    }
    return [];
};

/**
 * Converts GeoJSON coordinates to Google Maps LatLng format
 * GeoJSON uses [longitude, latitude], Google Maps uses {lat, lng}
 */
export const convertGeoJSONToGoogleMaps = (geometry: { type: string; coordinates: any }): GoogleMapsLatLng[] => {
    if (!geometry || !geometry.coordinates) {
        return [];
    }

    if (geometry.type === 'Polygon') {
        // Polygon: coordinates is [[[lng, lat], ...]]
        const ring = geometry.coordinates[0];
        if (!Array.isArray(ring)) return [];
        const paths = ring.map((coord: number[]) => ({
            lat: coord[1],
            lng: coord[0]
        }));
        return openPolygon(paths);
    } else if (geometry.type === 'MultiPolygon') {
        // MultiPolygon: coordinates is [[[[lng, lat], ...]]]
        // Return first polygon for now
        const firstPolygon = geometry.coordinates[0];
        if (!Array.isArray(firstPolygon) || !Array.isArray(firstPolygon[0])) return [];
        const ring = firstPolygon[0];
        const paths = ring.map((coord: number[]) => ({
            lat: coord[1],
            lng: coord[0]
        }));
        return openPolygon(paths);
    } else if (geometry.type === 'Point') {
        // Point: coordinates is [lng, lat]
        if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length !== 2) return [];
        return [{
            lat: geometry.coordinates[1],
            lng: geometry.coordinates[0]
        }];
    } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        return extractLineGeometryPaths(geometry as Geometry)[0] ?? [];
    }

    return [];
};

/**
 * Ensures a polygon is closed (first and last point are identical)
 * Google Maps requires polygons to be closed for proper rendering
 */
export const closePolygon = (paths: GoogleMapsLatLng[]): GoogleMapsLatLng[] => {
    if (paths.length < 3) return paths;
    const first = paths[0];
    const last = paths[paths.length - 1];
    
    // Check if polygon is already closed (with small tolerance for floating point)
    const isClosed = Math.abs(first.lat - last.lat) < 0.000001 && 
                     Math.abs(first.lng - last.lng) < 0.000001;
    
    if (!isClosed) {
        // Create a new array with the closing point
        const closedPaths = [...paths];
        // Add first point as last point to close the polygon
        closedPaths.push({ lat: first.lat, lng: first.lng });
        return closedPaths;
    }
    
    return paths;
};

/**
 * Normalizes a polygon path for interactive editing/rendering in Google Maps:
 * removes a duplicate closing point (last == first) if present.
 */
export const openPolygon = (paths: GoogleMapsLatLng[]): GoogleMapsLatLng[] => {
    if (!paths || paths.length < 2) return paths;
    const first = paths[0];
    const last = paths[paths.length - 1];

    const isClosed = Math.abs(first.lat - last.lat) < 0.000001 &&
        Math.abs(first.lng - last.lng) < 0.000001;

    return isClosed ? paths.slice(0, -1) : paths;
};

export const extractGeometryPoints = (geometry?: Geometry | null): GoogleMapsLatLng[] => {
    if (!geometry?.coordinates) {
        return [];
    }

    if (geometry.type === 'Point') {
        return convertGeoJSONToGoogleMaps(geometry);
    }

    if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        return extractLineGeometryPaths(geometry).flat();
    }

    if (geometry.type === 'Polygon') {
        const rings = geometry.coordinates as number[][][];
        return rings.flatMap((ring) => (
            Array.isArray(ring)
                ? ring.map((coord) => ({ lat: coord[1], lng: coord[0] }))
                : []
        ));
    }

    if (geometry.type === 'MultiPolygon') {
        const polygons = geometry.coordinates as unknown as number[][][][];
        return polygons.flatMap((polygon) => (
            Array.isArray(polygon)
                ? polygon.flatMap((ring) => (
                    Array.isArray(ring)
                        ? ring.map((coord) => ({ lat: coord[1], lng: coord[0] }))
                        : []
                ))
                : []
        ));
    }

    return [];
};

export const extractExteriorGeometryRings = (geometry?: Geometry | null): GoogleMapsLatLng[][] => {
    if (!geometry?.coordinates) {
        return [];
    }

    if (geometry.type === 'Polygon') {
        const exteriorRing = (geometry.coordinates as number[][][])[0];
        return Array.isArray(exteriorRing)
            ? [exteriorRing.map((coord) => ({ lat: coord[1], lng: coord[0] }))]
            : [];
    }

    if (geometry.type === 'MultiPolygon') {
        const polygons = geometry.coordinates as unknown as number[][][][];
        return polygons.flatMap((polygon) => {
            const exteriorRing = Array.isArray(polygon) ? polygon[0] : null;
            if (!Array.isArray(exteriorRing)) {
                return [];
            }

            return [
                exteriorRing.map((coord: number[]) => ({
                    lat: coord[1],
                    lng: coord[0]
                }))
            ];
        });
    }

    return [];
};

export const calculateGeometryBounds = (geometry?: Geometry | null) => {
    const points = extractGeometryPoints(geometry);
    if (points.length === 0) {
        return null;
    }

    const latitudes = points.map(point => point.lat);
    const longitudes = points.map(point => point.lng);
    const north = Math.max(...latitudes);
    const south = Math.min(...latitudes);
    const east = Math.max(...longitudes);
    const west = Math.min(...longitudes);

    return {
        north,
        south,
        east,
        west,
        center: {
            lat: (north + south) / 2,
            lng: (east + west) / 2
        }
    };
};

const calculateRingArea = (ring: GoogleMapsLatLng[]): number => {
    const closedRing = closePolygon(ring);
    let area = 0;

    for (let index = 0; index < closedRing.length - 1; index += 1) {
        const current = closedRing[index];
        const next = closedRing[index + 1];
        area += (current.lng * next.lat) - (next.lng * current.lat);
    }

    return area / 2;
};

export const getLargestExteriorGeometryRing = (geometry?: Geometry | null): GoogleMapsLatLng[] | null => {
    const rings = extractExteriorGeometryRings(geometry);
    if (rings.length === 0) {
        return null;
    }

    return rings.reduce<GoogleMapsLatLng[] | null>((largest, ring) => {
        if (!largest) {
            return ring;
        }

        const largestArea = Math.abs(calculateRingArea(largest));
        const ringArea = Math.abs(calculateRingArea(ring));
        return ringArea > largestArea ? ring : largest;
    }, null);
};

export const getGeometryCenter = (geometry?: Geometry | null): GoogleMapsLatLng | null => {
    if (!geometry) {
        return null;
    }

    if (geometry.type === 'Point') {
        const [point] = convertGeoJSONToGoogleMaps(geometry);
        return point ?? null;
    }

    const bounds = calculateGeometryBounds(geometry);
    return bounds?.center ?? null;
};

export const getGeometryLabelPosition = (geometry?: Geometry | null): GoogleMapsLatLng | null => {
    const ring = getLargestExteriorGeometryRing(geometry);
    if (!ring || ring.length < 3) {
        return getGeometryCenter(geometry);
    }

    const closedRing = closePolygon(ring);
    let twiceArea = 0;
    let centroidLngAccumulator = 0;
    let centroidLatAccumulator = 0;

    for (let index = 0; index < closedRing.length - 1; index += 1) {
        const current = closedRing[index];
        const next = closedRing[index + 1];
        const crossProduct = (current.lng * next.lat) - (next.lng * current.lat);

        twiceArea += crossProduct;
        centroidLngAccumulator += (current.lng + next.lng) * crossProduct;
        centroidLatAccumulator += (current.lat + next.lat) * crossProduct;
    }

    if (Math.abs(twiceArea) < 0.0000001) {
        return getGeometryCenter(geometry);
    }

    return {
        lng: centroidLngAccumulator / (3 * twiceArea),
        lat: centroidLatAccumulator / (3 * twiceArea)
    };
};

/**
 * Converts Google Maps LatLng coordinates to GeoJSON Polygon format
 * Ensures polygon is closed (first and last point are identical)
 */
export const convertGoogleMapsToGeoJSON = (coordinates: GoogleMapsLatLng[]): { type: 'Polygon'; coordinates: number[][][] } | null => {
    const open = openPolygon(coordinates);
    if (!open || open.length < 3) {
        return null;
    }

    // Convert to GeoJSON format [lng, lat]
    const geoJsonCoords = open.map(coord => [coord.lng, coord.lat]);

    // Ensure polygon is closed (first and last point should be identical)
    const firstPoint = geoJsonCoords[0];
    const lastPoint = geoJsonCoords[geoJsonCoords.length - 1];
    
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        geoJsonCoords.push([...firstPoint]);
    }

    return {
        type: 'Polygon',
        coordinates: [geoJsonCoords]
    };
};

/**
 * Validates polygon structure
 * Returns true if valid, false otherwise
 */
export const validatePolygon = (coordinates: GoogleMapsLatLng[]): { isValid: boolean; error?: string } => {
    const open = openPolygon(coordinates);
    if (!open || open.length < 3) {
        return {
            isValid: false,
            error: 'Polygon skal have minimum 3 punkter'
        };
    }

    // Check that all coordinates are valid numbers
    for (const coord of open) {
        if (typeof coord.lat !== 'number' || typeof coord.lng !== 'number') {
            return {
                isValid: false,
                error: 'Alle koordinater skal være gyldige tal'
            };
        }
        if (isNaN(coord.lat) || isNaN(coord.lng)) {
            return {
                isValid: false,
                error: 'Koordinater må ikke være NaN'
            };
        }
        if (coord.lat < -90 || coord.lat > 90) {
            return {
                isValid: false,
                error: 'Latitude skal være mellem -90 og 90'
            };
        }
        if (coord.lng < -180 || coord.lng > 180) {
            return {
                isValid: false,
                error: 'Longitude skal være mellem -180 og 180'
            };
        }
    }

    return { isValid: true };
};

/** Map public API pt codes to parking_type strings for map/sidebar. */
const MAP_PT_TO_PARKING_TYPE: Record<string, string> = {
    f: 'free',
    p: 'paid',
    lt: 'limited_time',
};

/**
 * Normalize public API map-view item (id, g, pt, ld) to shape expected by ParkingLotMap
 * (geometry, parking_type, _id, lotId). Use for drawing polygons; fetch full lot on click for sidebar.
 */
export function normalizeMapItemToParkingLot(
    item: ParkingLotMapItem
): Pick<ParkingLot, '_id' | 'lotId' | 'geometry' | 'parking_type'> {
    return {
        _id: item.id,
        lotId: item.id,
        geometry: item.g as Geometry,
        parking_type: MAP_PT_TO_PARKING_TYPE[item.pt] ?? 'free',
    };
}

/**
 * Returns polygon color based on parking type
 */
export const getPolygonColor = (parkingType?: string): string => {
    switch (parkingType) {
        case 'free':
            return '#00800A'; // green
        case 'paid':
            return '#1672FF'; // blue
        case 'limited_time':
            return '#df9626'; // orange
        default:
            return '#2196F3'; // blue
    }
};
